import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useApiClient } from '@/hooks/useApiClient';
import { useToast } from '@/contexts/ToastContext';
import { useAuthStore } from '@/store/auth.store';
import { LoadingSpinner, PageError } from '@/components/ui';
import { CreatePreparationRecetteForm } from './components/CreatePreparationRecetteForm';
import { AuthenticatedImage } from '@/components/ui';
import type { PreparationBatch } from '@haccp/api-client';

const PlusIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
  </svg>
);
const PrintIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
  </svg>
);
const TrashIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);
const ChefHatIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6a4 4 0 014-4 4 4 0 010 8H8a4 4 0 010-8 4 4 0 014 4z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 10v10h8V10" />
  </svg>
);
const ClockIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2m4-2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

function statusColor(status: PreparationBatch['status']): string {
  switch (status) {
    case 'EXPIRED': return 'bg-red-100 text-red-800 border-red-200';
    case 'EXPIRING_SOON': return 'bg-amber-100 text-amber-800 border-amber-200';
    case 'DESTROYED': return 'bg-slate-100 text-slate-600 border-slate-200';
    default: return 'bg-green-100 text-green-800 border-green-200';
  }
}

function statusLabel(status: PreparationBatch['status'], t: (k: string) => string): string {
  const map: Record<string, string> = {
    PREPARED: t('recettes.statusPrepared'),
    VALID: t('recettes.statusValid'),
    EXPIRING_SOON: t('recettes.statusExpiringSoon'),
    EXPIRED: t('recettes.statusExpired'),
    DESTROYED: t('recettes.statusDestroyed'),
    STORED: t('recettes.statusStored'),
  };
  return map[status] || status;
}

export default function PreparationRecettesPage() {
  const { t, i18n } = useTranslation();
  const apiClient = useApiClient();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);

  const [showCreate, setShowCreate] = useState(false);
  const [printingId, setPrintingId] = useState<string | null>(null);
  const [confirmDestroyId, setConfirmDestroyId] = useState<string | null>(null);

  const { data: batches = [], isLoading, error } = useQuery<PreparationBatch[]>({
    queryKey: ['preparation-recettes'],
    queryFn: () => apiClient.listBatches(),
  });

  const destroyMutation = useMutation({
    mutationFn: (id: string) => apiClient.destroyBatch(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['preparation-recettes'] });
      setConfirmDestroyId(null);
      addToast(t('recettes.destroySuccess'), 'success');
    },
    onError: () => addToast(t('recettes.destroyError'), 'error'),
  });

  const handlePrint = async (batch: PreparationBatch) => {
    try {
      setPrintingId(batch.id);
      const response = await fetch(`/api/dlc/batches/${batch.id}/label`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error();
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `etiquette-${batch.batchId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      addToast(t('recettes.printError'), 'error');
    } finally {
      setPrintingId(null);
    }
  };

  const canCreate = user?.role === 'OWNER' || user?.role === 'STAFF';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 sm:p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-orange-100 rounded-lg">
              <ChefHatIcon className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{t('recettes.title')}</h1>
              <p className="text-slate-600">{t('recettes.subtitle')}</p>
            </div>
          </div>
          {canCreate && (
            <button
              onClick={() => setShowCreate(true)}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition"
            >
              <PlusIcon className="w-5 h-5" />
              {t('recettes.addBtn')}
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <LoadingSpinner message={t('recettes.loading')} />
      ) : error ? (
        <PageError message={t('recettes.loadError')} />
      ) : batches.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-dashed border-slate-300">
          <ChefHatIcon className="w-12 h-12 text-slate-400 mx-auto mb-3" />
          <p className="text-slate-600 text-lg">{t('recettes.empty')}</p>
          {canCreate && (
            <button
              onClick={() => setShowCreate(true)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-orange-100 text-orange-600 rounded-lg hover:bg-orange-200 transition"
            >
              <PlusIcon className="w-5 h-5" />
              {t('recettes.createFirst')}
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {batches.map((batch) => {
            const batchName = batch.name || batch.internalPreparation?.name || '-';
            const startDate = new Date(batch.productionDateTime);
            const endDate = new Date(batch.dlc);
            const isActive = batch.status !== 'DESTROYED' && batch.status !== 'EXPIRED';

            return (
              <div
                key={batch.id}
                className={`bg-white rounded-xl border shadow-sm overflow-hidden ${batch.status === 'DESTROYED' ? 'opacity-60' : ''}`}
              >
                {/* Photo */}
                {batch.photoUrl && (
                  <div className="h-36 overflow-hidden bg-slate-100">
                    <AuthenticatedImage
                      src={batch.photoUrl}
                      alt={batchName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}

                <div className="p-4">
                  {/* Name + status */}
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <h3 className="font-bold text-slate-900 text-base leading-tight">{batchName}</h3>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full border flex-shrink-0 ${statusColor(batch.status)}`}>
                      {statusLabel(batch.status, t)}
                    </span>
                  </div>

                  {/* Dates */}
                  <div className="space-y-1.5 mb-3">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <ClockIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <span className="text-xs text-slate-500">{t('recettes.startLabel')}:</span>
                      <span className="font-medium text-slate-800">
                        {startDate.toLocaleString(i18n.language, { dateStyle: 'short', timeStyle: 'short' })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <ClockIcon className="w-4 h-4 text-red-400 flex-shrink-0" />
                      <span className="text-xs text-slate-500">{t('recettes.endLabel')}:</span>
                      <span className={`font-bold ${batch.status === 'EXPIRED' ? 'text-red-700' : batch.status === 'EXPIRING_SOON' ? 'text-amber-700' : 'text-slate-800'}`}>
                        {endDate.toLocaleString(i18n.language, { dateStyle: 'short', timeStyle: 'short' })}
                      </span>
                    </div>
                  </div>

                  {/* Quantity + created by */}
                  <div className="flex items-center justify-between text-sm text-slate-500 mb-3">
                    <span>{batch.quantity} {batch.unit}</span>
                    <span className="text-xs">{batch.createdBy?.displayName || batch.createdBy?.email || ''}</span>
                  </div>

                  {/* Actions */}
                  {isActive && (
                    <div className="flex gap-2 pt-2 border-t border-slate-100">
                      <button
                        onClick={() => handlePrint(batch)}
                        disabled={printingId === batch.id}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition disabled:opacity-50"
                      >
                        <PrintIcon className="w-4 h-4" />
                        {printingId === batch.id ? t('recettes.printing') : t('recettes.printLabel')}
                      </button>
                      {canCreate && (
                        <button
                          onClick={() => setConfirmDestroyId(batch.id)}
                          className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition"
                        >
                          <TrashIcon className="w-4 h-4" />
                          {t('recettes.destroy')}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Confirm destroy */}
                  {confirmDestroyId === batch.id && (
                    <div className="mt-2 p-3 bg-red-50 rounded-lg border border-red-200">
                      <p className="text-sm font-medium text-red-900 mb-2">{t('recettes.confirmDestroy')}</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setConfirmDestroyId(null)}
                          className="flex-1 px-3 py-1.5 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition"
                        >
                          {t('common.cancel')}
                        </button>
                        <button
                          onClick={() => destroyMutation.mutate(batch.id)}
                          disabled={destroyMutation.isPending}
                          className="flex-1 px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
                        >
                          {destroyMutation.isPending ? t('recettes.destroying') : t('recettes.confirmDestroyBtn')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreate && (
        <CreatePreparationRecetteForm
          onClose={() => setShowCreate(false)}
          onSuccess={() => setShowCreate(false)}
        />
      )}
    </div>
  );
}
